import React, { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

const DateRangePicker = ({ startDate, endDate, onRangeChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  // 프리셋 계산 로직
  const handlePreset = (type) => {
    const now = new Date();
    let start = startDate ? new Date(startDate) : new Date();
    let end = endDate ? new Date(endDate) : new Date();

    switch (type) {
      case 'today': 
        start = new Date(); 
        end = new Date(); 
        break;
      case '1day': 
        // 현재 start 날짜에서 1일을 뺌 (연속 클릭 시 계속 과거로 감)
        start.setDate(start.getDate() - 1); 
        end = new Date(start); // 종료일도 시작일과 동일하게 맞춤 (필요에 따라 조정)
        break;
      case '1week': 
        end = new Date(start);
        start.setDate(start.getDate() - 7); 
        break;
      case '1month': 
        end = new Date(start);
        start.setMonth(start.getMonth() - 1); 
        break;
      case 'prevMonth':
        // 현재 기준 이전 달의 1일부터 말일까지
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      default: break;
    }
    
    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    onRangeChange(formatDate(start), formatDate(end));
    setIsOpen(false);
  };

  return (
    <div className="relative w-full md:w-auto text-slate-700">
      {/* 1. iOS 전용 스타일 주입 */}
      <style>{`
        input[type="date"] {
          -webkit-appearance: none;
          display: block;
          background-image: none;
        }
        /* iOS에서 날짜 텍스트가 왼쪽으로 정렬되도록 강제 */
        input[type="date"]::-webkit-date-and-time-value {
          text-align: left;
          margin: 0;
        }
      `}</style>

      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-xl border border-slate-200 shadow-sm hover:bg-slate-50 transition-all text-sm font-medium w-full md:w-auto justify-between"
      >
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-blue-600" />
          <span>{startDate === endDate ? startDate : `${startDate} ~ ${endDate}`}</span>
        </div>
        <ChevronDown size={16} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[90]" onClick={() => setIsOpen(false)} />
          
          {/* 부모 박스: overflow-hidden 추가로 튀어나온 자식 방지 */}
            <div className="
            /* 공통 스타일 */
            absolute p-4 bg-white rounded-2xl shadow-xl border border-slate-100 z-[100] 
            overflow-hidden animate-in fade-in zoom-in-95
            
            /* 위치 조정 핵심: mt(마진)를 키우거나 top에 여유를 줍니다 */
            mt-5 /* 기존 mt-2에서 mt-5로 변경 (약 20px 아래로) */
            
            /* 모바일 (중앙 정렬) */
            fixed left-1/2 -translate-x-1/2 w-[calc(100vw-2rem)] 
            /* 모바일에서 버튼 바로 아래에 붙이고 싶다면 top-full 유지, 
                화면 중앙 근처로 내리고 싶다면 top-[20%] 등으로 조절 가능 */
            top-full 

            /* 데스크탑 (md 이상) */
            md:absolute md:left-auto md:right-0 md:translate-x-0 md:w-[320px] 
            md:top-full md:origin-top-right
            ">
            
            <div className="grid grid-cols-3 gap-2 mb-4">
              {['today', '1day', '1week', '1month', 'prevMonth'].map((type) => (
                <button 
                  key={type}
                  onClick={() => handlePreset(type)}
                  className="py-1.5 px-2 bg-slate-50 hover:bg-blue-50 hover:text-blue-600 rounded-lg text-xs font-semibold transition-colors"
                >
                  {type === 'today' ? '오늘' : type === '1day' ? '하루 전' : type === '1week' ? '한주 전' : type === '1month' ? '한달 전' : '이전 달'}
                </button>
              ))}
            </div>

            <hr className="mb-4 border-slate-100" />

            <div className="space-y-3">
              {[
                { label: '시작일', val: startDate, setter: (v) => onRangeChange(v, endDate) },
                { label: '종료일', val: endDate, setter: (v) => onRangeChange(startDate, v) }
              ].map((item, idx) => (
                <div key={idx} className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase ml-1">{item.label}</span>
                    <input 
                    type="date" 
                    value={item.val}
                    onChange={(e) => item.setter(e.target.value)}
                    // 클릭 시 브라우저의 네이티브 피커를 강제로 호출
                    onClick={(e) => {
                        try {
                        e.target.showPicker(); 
                        } catch (error) {
                        // showPicker를 지원하지 않는 오래된 브라우저 대응
                        console.log("Picker not supported");
                        }
                    }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-base md:text-sm focus:ring-2 focus:ring-blue-500/20 outline-none bg-white relative
                                /* 아이콘 영역을 전체로 확장하여 클릭 감도 향상 */
                                [&::-webkit-calendar-picker-indicator]:absolute
                                [&::-webkit-calendar-picker-indicator]:inset-0
                                [&::-webkit-calendar-picker-indicator]:w-full
                                [&::-webkit-calendar-picker-indicator]:h-full
                                [&::-webkit-calendar-picker-indicator]:cursor-pointer
                                [&::-webkit-calendar-picker-indicator]:opacity-0" 
                    />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DateRangePicker;